import React from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useRouteMatch } from 'react-router-dom';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import {
  Drawer,
  Tab,
  Tabs,
} from '@material-ui/core/';
import {
  Home as HomeIcon,
  Toc as TocIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountIcon,
  Backup as ImportIcon,
} from '@material-ui/icons';
import { selectOpen } from '../../store/layoutSlice';

import logo from '../../assets/guest-ranger-logo-white.svg';
import mobileLogo from '../../assets/guest-ranger-logo-mobile-white.svg';

const drawerWidth = 180;
const mobileDrawerWidth = 60;

const useStyles = makeStyles((theme) => ({
  grow: {
    flexGrow: 1,
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 64,
    marginLeft: 5,
    marginRight: 5,
  },
  logo: {
    width: 135,
    maxWidth: '100%',
  },
  toolbar: {
    paddingRight: 24, // keep right padding when drawer closed
  },
  toolbarIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 8px',
    ...theme.mixins.toolbar,
  },
  drawerPaper: {
    position: 'relative',
    whiteSpace: 'nowrap',
    height: '100vh',
    width: mobileDrawerWidth,
    backgroundColor: theme.palette.primary.main,
    border: 0,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    flexDirection: 'column',
    justifyContent: 'center',

    [theme.breakpoints.up('md')]: {
      width: drawerWidth,
    },
  },
  drawerPaperClose: {
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9),
    },
  },
  button: {
    '&active': {
      background: 'red',
    },
  },
  secondTabList: {
    marginBottom: theme.spacing(3),
  },
  tab: {
    color: 'white',
    opacity: 1,
    minHeight: 56,
    paddingLeft: theme.spacing(2),
    minWidth: theme.spacing(7),

    [theme.breakpoints.up('md')]: {
      minWidth: theme.spacing(9),
    },

    '&.Mui-selected': {
      color: theme.palette.primary.main,
    },

    '& .MuiTab-wrapper': {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      fontSize: 0,

      [theme.breakpoints.up('md')]: {
        fontSize: '.875rem',
      },
    },

    '& .MuiTab-wrapper > *:first-child': {
      fontSize: 32,
      marginBottom: 0,
      marginRight: 6,
    },
  },
  indicator: {
    left: 0,
    width: mobileDrawerWidth,
    zIndex: -1,

    [theme.breakpoints.up('md')]: {
      width: drawerWidth - 20,
      borderTopRightRadius: 56,
      borderBottomRightRadius: 56,
    },
  },
}));

export default function AppDrawer() {
  const classes = useStyles();
  const open = useSelector(selectOpen);

  const tabList1 = [
    {
      route: '/',
      label: 'Home',
      icon: <HomeIcon />,
      disabled: false,
    },
    {
      route: '/create-verification',
      label: 'Widget',
      icon: <AccountIcon />,
      disabled: false,
    },
    {
      route: '/verifications',
      label: 'Verifications',
      icon: <TocIcon />,
      disabled: false,
    },
    {
      route: '/import',
      label: 'Import',
      icon: <ImportIcon />,
      disabled: false,
    },
  ];

  const tabList2 = [
    {
      route: '/settings',
      label: 'Settings',
      icon: <SettingsIcon />,
      disabled: false,
      position: 'end',
    },
  ];

  // eslint-disable-next-line max-len
  const routeMatch = useRouteMatch(['/knowledge-base', ...tabList2.map((t) => (t.route)).reverse(), ...tabList1.map((t) => (t.route)).reverse()]);
  const currentTab = routeMatch?.path;

  return (
    <Drawer
      variant="permanent"
      classes={{
        paper: clsx(classes.drawerPaper, !open && classes.drawerPaperClose),
      }}
      open={open}
    >
      <a className={classes.link} href="https://guestranger.com/" target="_blank" rel="noreferrer">
        <picture className={classes.logoContainer}>
          <source media="(min-width:769px)" srcSet={logo} />
          <img src={mobileLogo} alt="Guest Ranger" className={classes.logo} />
        </picture>
      </a>
      <div className={classes.grow} />
      <Tabs
        classes={{ indicator: classes.indicator }}
        value={currentTab}
        orientation="vertical"
        TabIndicatorProps={{ style: { background: 'white' } }}
      >
        {tabList1.map(({
          route,
          label,
          icon,
          disabled,
        }) => (
          <Tab
            className={classes.tab}
            icon={icon}
            value={route}
            to={route}
            component={NavLink}
            label={label}
            aria-label={label}
            disabled={disabled}
          />
        ))}
      </Tabs>
      <div className={classes.grow} />
      <Tabs
        className={classes.secondTabList}
        classes={{ indicator: classes.indicator }}
        value={currentTab}
        orientation="vertical"
        TabIndicatorProps={{ style: { background: 'white' } }}
      >
        {tabList2.map(({
          route,
          label,
          icon,
          disabled,
        }) => (
          <Tab
            className={classes.tab}
            icon={icon}
            value={route}
            to={route}
            component={NavLink}
            label={label}
            aria-label={label}
            disabled={disabled}
          />
        ))}
      </Tabs>
    </Drawer>
  );
}
